/**
 * 支付模块相关 Store
 * 负责支付订单数据的加载、管理和操作
 */

import { defineStore } from 'pinia'
import { ref, computed, shallowRef } from 'vue'
import { useCacheStore } from '../cache'
import { paymentsApi } from '@/https/api/payments'
import { convertCheckStatusToListItem, PAYMENT_CONSTANTS } from '@/utils/paymentHelpers'
import {
    PAYMENT_ORDER_STATUS_LABELS,
    PAYMENT_METHOD_LABELS
} from '@/https/types/payments'
import type {
    PaymentOrderListRequest,
    PaymentOrderMySalesRequest,
    PaymentCreateRentalRequest,
    PaymentBuyerCompleteRequest,
    PaymentSellerConfirmRequest,
    PaymentUserCancelOrderRequest,
    PaymentFinalConfirmRequest,
    PaymentCheckStatusResponse,
    PaymentOrderListItem,
    PaymentOrderDetail,
    PaymentOrderStatusEnum,
    PaymentMethodEnum,
    OrderStatistics
} from '@/https/types/payments'

export const usePaymentsStore = defineStore('payments', () => {
    const cacheStore = useCacheStore()

    // ==================== 状态管理 ====================

    // 支付订单列表（买家视角）
    const paymentOrderList = ref<PaymentOrderListItem[]>([])

    // 销售订单列表（卖家视角）
    const salesOrderList = ref<PaymentOrderListItem[]>([])

    // 订单详情缓存
    const orderDetailCache = ref<Map<number, PaymentOrderDetail>>(new Map())

    // 当前选中的订单
    const currentOrder = ref<PaymentOrderDetail | null>(null)

    // 分页信息 - 支付订单列表
    const paymentOrderPagination = ref<{
        total: number
        page: number
        size: number
        has_more: boolean
    }>({
        total: 0,
        page: 1,
        size: 20,
        has_more: false
    })

    // 分页信息 - 销售订单列表
    const salesOrderPagination = ref<{
        total: number
        page: number
        size: number
        has_more: boolean
    }>({
        total: 0,
        page: 1,
        size: 20,
        has_more: false
    })

    // 查询参数
    const lastPaymentOrderQuery = ref<PaymentOrderListRequest>({})
    const lastSalesOrderQuery = ref<PaymentOrderMySalesRequest>({})

    // 加载状态
    const paymentOrderListLoading = ref(false)
    const salesOrderListLoading = ref(false)
    const orderDetailLoading = ref<Set<number>>(new Set())
    const orderOperationLoading = ref(false)

    // 错误信息
    const paymentOrderListError = ref<string | null>(null)
    const salesOrderListError = ref<string | null>(null)
    const orderDetailError = ref<Map<number, string>>(new Map())

    // 轮询相关状态（使用 shallowRef 避免 Map 键值被代理导致的 raw/reactive 警告）
    const paymentStatusPolling = shallowRef<Map<number, boolean>>(new Map())
    
    // 轮询状态回调
    const paymentStatusCallbacks = shallowRef<Map<number, (data: PaymentCheckStatusResponse) => void>>(new Map())

    // 订单统计数据
    const orderStatistics = ref<OrderStatistics | null>(null)
    const orderStatisticsLoading = ref(false)
    const orderStatisticsError = ref<string | null>(null)

    // ==================== 计算属性 ====================

    // 是否有支付订单数据
    const hasPaymentOrderList = computed(() => paymentOrderList.value.length > 0)

    // 是否有销售订单数据
    const hasSalesOrderList = computed(() => salesOrderList.value.length > 0)

    // 支付订单总数
    const paymentOrderCount = computed(() => paymentOrderPagination.value.total)

    // 销售订单总数
    const salesOrderCount = computed(() => salesOrderPagination.value.total)

    // 是否可以进行订单操作
    const canOperateOrder = computed(() => !orderOperationLoading.value)

    // 按状态分组的支付订单
    const paymentOrderListByStatus = computed(() => {
        const groups: Record<string, PaymentOrderListItem[]> = {}
        paymentOrderList.value.forEach(order => {
            const status = order.status
            if (!groups[status]) {
                groups[status] = []
            }
            groups[status].push(order)
        })
        return groups
    })

    // 按状态分组的销售订单
    const salesOrderListByStatus = computed(() => {
        const groups: Record<string, PaymentOrderListItem[]> = {}
        salesOrderList.value.forEach(order => {
            const status = order.status
            if (!groups[status]) {
                groups[status] = []
            }
            groups[status].push(order)
        })
        return groups
    })

    // 待支付的订单
    const pendingPaymentOrders = computed(() => {
        return paymentOrderList.value.filter(order => order.status === 'PENDING')
    })

    // 租赁中的订单
    const rentingOrders = computed(() => {
        return paymentOrderList.value.filter(order => order.status === 'PAID')
    })

    // 待确认的销售订单
    const pendingConfirmOrders = computed(() => {
        return salesOrderList.value.filter(order => order.status === 'BUYER_COMPLETED')
    })

    // 格式化的支付订单列表
    const formattedPaymentOrderList = computed(() => {
        return paymentOrderList.value.map(order => ({
            ...order,
            formattedTotalAmount: (order.total_amount / 100).toFixed(2),
            formattedRentalAmount: (order.rental_amount / 100).toFixed(2),
            formattedDepositAmount: (order.deposit_amount / 100).toFixed(2),
            formattedCommissionAmount: (order.commission_amount / 100).toFixed(2),
            formattedConsumptionAmount: (order.consumption_amount / 100).toFixed(2),
            formattedStatus: getOrderStatusText(order.status),
            canCancel: order.can_cancel === 'true',
            canConfirm: order.can_confirm === 'true'
        }))
    })

    // 格式化的订单统计数据
    const formattedOrderStatistics = computed(() => {
        if (!orderStatistics.value) return null

        return {
            ...orderStatistics.value
        }
    })

    // ==================== 数据加载 ====================

    /**
     * 加载支付订单列表
     */
    const loadPaymentOrderList = async (params?: PaymentOrderListRequest) => {
        try {
            paymentOrderListLoading.value = true
            paymentOrderListError.value = null

            const query = { ...lastPaymentOrderQuery.value, ...params }
            lastPaymentOrderQuery.value = query

            const response = await paymentsApi.getPaymentOrderList(query)

            paymentOrderList.value = response.items
            paymentOrderPagination.value = {
                total: response.total,
                page: response.page,
                size: response.size,
                has_more: response.has_more
            }

            // 缓存订单数据
            response.items.forEach(order => {
                cacheStore.setCache(`payment_order_${order.id}`, order, 5 * 60 * 1000) // 5分钟缓存
            })

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载支付订单列表失败'
            paymentOrderListError.value = errorMessage
            console.error('加载支付订单列表失败:', error)
            throw error
        } finally {
            paymentOrderListLoading.value = false
        }
    }

    /**
     * 加载销售订单列表
     */
    const loadSalesOrderList = async (params?: PaymentOrderMySalesRequest) => {
        try {
            salesOrderListLoading.value = true
            salesOrderListError.value = null

            const query = { ...lastSalesOrderQuery.value, ...params }
            lastSalesOrderQuery.value = query

            const response = await paymentsApi.getMySalesOrderList(query)

            salesOrderList.value = response.items
            salesOrderPagination.value = {
                total: response.total,
                page: response.page,
                size: response.size,
                has_more: response.has_more
            }

            // 缓存订单数据
            response.items.forEach(order => {
                cacheStore.setCache(`sales_order_${order.id}`, order, 5 * 60 * 1000) // 5分钟缓存
            })

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载销售订单列表失败'
            salesOrderListError.value = errorMessage
            console.error('加载销售订单列表失败:', error)
            throw error
        } finally {
            salesOrderListLoading.value = false
        }
    }

    /**
     * 加载订单详情
     */
    const loadOrderDetail = async (id: number, forceRefresh = false) => {
        // 检查缓存
        if (!forceRefresh && orderDetailCache.value.has(id)) {
            const cached = orderDetailCache.value.get(id)!
            currentOrder.value = cached
            return cached
        }

        try {
            orderDetailLoading.value.add(id)
            orderDetailError.value.delete(id)

            const detail = await paymentsApi.getPaymentOrderDetail(id)

            // 更新缓存
            orderDetailCache.value.set(id, detail)
            currentOrder.value = detail

            // 设置缓存过期时间
            cacheStore.setCache(`order_detail_${id}`, detail, 10 * 60 * 1000) // 10分钟缓存

            return detail
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载订单详情失败'
            orderDetailError.value.set(id, errorMessage)
            console.error(`加载订单详情失败 (ID: ${id}):`, error)
            throw error
        } finally {
            orderDetailLoading.value.delete(id)
        }
    }

    /**
     * 刷新支付订单列表
     */
    const refreshPaymentOrderList = async () => {
        await loadPaymentOrderList(lastPaymentOrderQuery.value)
    }

    /**
     * 刷新销售订单列表
     */
    const refreshSalesOrderList = async () => {
        await loadSalesOrderList(lastSalesOrderQuery.value)
    }

    /**
     * 加载订单统计数据
     */
    const loadOrderStatistics = async (forceRefresh = false) => {
        // 检查缓存
        if (!forceRefresh && orderStatistics.value) {
            const cacheKey = 'order_statistics'
            const cached = cacheStore.getCache<OrderStatistics>(cacheKey)
            if (cached) {
                orderStatistics.value = cached
                return cached
            }
        }

        try {
            orderStatisticsLoading.value = true
            orderStatisticsError.value = null

            const statistics = await paymentsApi.getOrderStatistics()

            orderStatistics.value = statistics

            // 缓存统计数据（缓存时间较短，因为统计数据变化频繁）
            cacheStore.setCache('order_statistics', statistics, 2 * 60 * 1000) // 2分钟缓存

            return statistics
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '加载订单统计失败'
            orderStatisticsError.value = errorMessage
            console.error('加载订单统计失败:', error)
            throw error
        } finally {
            orderStatisticsLoading.value = false
        }
    }

    /**
     * 刷新订单统计数据
     */
    const refreshOrderStatistics = async () => {
        await loadOrderStatistics(true)
    }

    // ==================== 订单操作 ====================

    /**
     * 创建租赁订单
     */
    const createRentalOrder = async (data: PaymentCreateRentalRequest) => {
        try {
            orderOperationLoading.value = true

            const result = await paymentsApi.createRentalOrder(data)

            // 更新当前订单
            currentOrder.value = result

            // 更新缓存
            orderDetailCache.value.set(result.id, result)

            return result
        } catch (error) {
            console.error('创建租赁订单失败:', error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 租客确认完成租赁
     */
    const buyerCompleteRental = async (id: number, data?: PaymentBuyerCompleteRequest) => {
        try {
            orderOperationLoading.value = true

            await paymentsApi.buyerCompleteRental(id, data)

            // 刷新订单详情和列表
            await loadOrderDetail(id, true)
            await refreshPaymentOrderList()

        } catch (error) {
            console.error('租客确认完成失败:', error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 卖家确认归还
     */
    const sellerConfirmReturn = async (id: number, data: PaymentSellerConfirmRequest) => {
        try {
            orderOperationLoading.value = true

            await paymentsApi.sellerConfirmReturn(id, data)

            // 刷新订单详情和列表
            await loadOrderDetail(id, true)
            await refreshSalesOrderList()

        } catch (error) {
            console.error('卖家确认归还失败:', error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 取消订单
     */
    const cancelOrder = async (id: number, data?: PaymentUserCancelOrderRequest) => {
        try {
            orderOperationLoading.value = true

            await paymentsApi.cancelOrder(id, data)

            // 刷新订单详情和列表
            await loadOrderDetail(id, true)
            await refreshPaymentOrderList()

        } catch (error) {
            console.error('取消订单失败:', error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 最终确认交易完成
     */
    const finalConfirmOrder = async (id: number, data?: PaymentFinalConfirmRequest) => {
        try {
            orderOperationLoading.value = true

            await paymentsApi.finalConfirm(id, data)

            // 刷新订单详情和列表
            await loadOrderDetail(id, true)
            await refreshSalesOrderList()

        } catch (error) {
            console.error('最终确认失败:', error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 删除订单
     */
    const deleteOrder = async (id: number) => {
        try {
            orderOperationLoading.value = true

            await paymentsApi.deleteOrder(id)

            // 从列表中移除订单
            const paymentIndex = paymentOrderList.value.findIndex(order => order.id === id)
            if (paymentIndex !== -1) {
                paymentOrderList.value.splice(paymentIndex, 1)
                paymentOrderPagination.value.total -= 1
            }

            const salesIndex = salesOrderList.value.findIndex(order => order.id === id)
            if (salesIndex !== -1) {
                salesOrderList.value.splice(salesIndex, 1)
                salesOrderPagination.value.total -= 1
            }

            // 清除订单详情缓存
            orderDetailCache.value.delete(id)
            if (currentOrder.value?.id === id) {
                currentOrder.value = null
            }

            // 清除相关缓存
            cacheStore.removeCache(`payment_order_${id}`)
            cacheStore.removeCache(`sales_order_${id}`)
            cacheStore.removeCache(`order_detail_${id}`)

        } catch (error: unknown) {
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    /**
     * 取消退款申请
     */
    const cancelRefund = async (id: number, reason?: string) => {
        try {
            orderOperationLoading.value = true

            await paymentsApi.cancelRefund(id, { reason })

            // 刷新订单详情和列表
            await loadOrderDetail(id, true)
            await refreshPaymentOrderList()

        } catch (error) {
            console.error('取消退款申请失败:', error)
            throw error
        } finally {
            orderOperationLoading.value = false
        }
    }

    // ==================== 支付状态轮询 ====================

    /**
     * 开始轮询支付状态
     */
    const startPaymentStatusPolling = (orderId: number, interval = PAYMENT_CONSTANTS.POLLING_INTERVAL, callback?: (data: PaymentCheckStatusResponse) => void) => {
        if (paymentStatusPolling.value.get(orderId)) return

        paymentStatusPolling.value.set(orderId, true)
        
        // 保存回调函数
        if (callback) {
            paymentStatusCallbacks.value.set(orderId, callback)
        }

        const pollStatus = async () => {
            try {
                if (!paymentStatusPolling.value.get(orderId)) return

                const status = await paymentsApi.checkPaymentStatus(orderId)

                // 调用回调函数，传递完整状态数据
                const callback = paymentStatusCallbacks.value.get(orderId)
                if (callback) {
                    callback({ ...status })
                }

                // 使用工具函数进行类型转换
                const orderUpdate = convertCheckStatusToListItem(status)

                // 查找并更新订单列表中的对应项
                const paymentIndex = paymentOrderList.value.findIndex(order => order.id === status.order_id)
                if (paymentIndex !== -1) {
                    Object.assign(paymentOrderList.value[paymentIndex], orderUpdate)
                }

                const salesIndex = salesOrderList.value.findIndex(order => order.id === status.order_id)
                if (salesIndex !== -1) {
                    Object.assign(salesOrderList.value[salesIndex], orderUpdate)
                }

                // 如果已支付或取消，停止轮询
                if (status.status === 'PAID' || status.status === 'CANCELLED' || status.status === 'REFUNDED') {
                    stopPaymentStatusPolling(orderId)
                    // 刷新订单详情
                    await loadOrderDetail(orderId, true)
                } else {
                    // 继续轮询
                    setTimeout(pollStatus, interval)
                }
            } catch (error) {
                console.error('轮询支付状态失败:', error)
                setTimeout(pollStatus, interval * 2) // 出错时延长间隔
            }
        }

        pollStatus()
    }

    /**
     * 停止轮询支付状态
     */
    const stopPaymentStatusPolling = (orderId: number) => {
        paymentStatusPolling.value.set(orderId, false)
        // 清理回调函数
        paymentStatusCallbacks.value.delete(orderId)
    }

    /**
     * 停止所有订单的轮询（兜底）
     */
    const stopAllPaymentStatusPolling = () => {
        paymentStatusPolling.value.forEach((_, id) => {
            paymentStatusPolling.value.set(id, false)
        })
        paymentStatusCallbacks.value.clear()
    }

    // ==================== 工具方法 ====================

    /**
     * 根据ID查找支付订单
     */
    const findPaymentOrderById = (id: number): PaymentOrderListItem | null => {
        return paymentOrderList.value.find(order => order.id === id) || null
    }

    /**
     * 根据ID查找销售订单
     */
    const findSalesOrderById = (id: number): PaymentOrderListItem | null => {
        return salesOrderList.value.find(order => order.id === id) || null
    }

    /**
     * 更新列表中的订单
     */
    const updateOrderInList = (updatedOrder: PaymentOrderListItem) => {
        // 更新支付订单列表
        const paymentIndex = paymentOrderList.value.findIndex(order => order.id === updatedOrder.id)
        if (paymentIndex !== -1) {
            paymentOrderList.value[paymentIndex] = updatedOrder
        }

        // 更新销售订单列表
        const salesIndex = salesOrderList.value.findIndex(order => order.id === updatedOrder.id)
        if (salesIndex !== -1) {
            salesOrderList.value[salesIndex] = updatedOrder
        }
    }

    /**
     * 获取订单状态文本
     */
    const getOrderStatusText = (status: PaymentOrderStatusEnum): string => {
        return PAYMENT_ORDER_STATUS_LABELS[status] || status
    }

    /**
     * 获取支付方式文本
     */
    const getPaymentMethodText = (method: PaymentMethodEnum): string => {
        return PAYMENT_METHOD_LABELS[method] || method
    }

    // ==================== 状态重置 ====================

    /**
     * 清除订单缓存
     */
    const clearOrderCache = () => {
        orderDetailCache.value.clear()
        currentOrder.value = null
    }

    /**
     * 重置支付订单列表状态
     */
    const resetPaymentOrderListState = () => {
        paymentOrderList.value = []
        paymentOrderPagination.value = {
            total: 0,
            page: 1,
            size: 20,
            has_more: false
        }
        lastPaymentOrderQuery.value = {}
        paymentOrderListError.value = null
    }

    /**
     * 重置销售订单列表状态
     */
    const resetSalesOrderListState = () => {
        salesOrderList.value = []
        salesOrderPagination.value = {
            total: 0,
            page: 1,
            size: 20,
            has_more: false
        }
        lastSalesOrderQuery.value = {}
        salesOrderListError.value = null
    }

    /**
     * 重置所有状态
     */
    const resetAllState = () => {
        resetPaymentOrderListState()
        resetSalesOrderListState()
        clearOrderCache()
        orderDetailError.value.clear()

        // 停止所有轮询
        paymentStatusPolling.value.forEach((_, orderId) => {
            stopPaymentStatusPolling(orderId)
        })
        paymentStatusPolling.value.clear()
        paymentStatusCallbacks.value.clear()
    }

    return {
        // 状态
        paymentOrderList,
        salesOrderList,
        orderDetailCache,
        currentOrder,
        paymentOrderPagination,
        salesOrderPagination,
        lastPaymentOrderQuery,
        lastSalesOrderQuery,
        paymentOrderListLoading,
        salesOrderListLoading,
        orderDetailLoading,
        orderOperationLoading,
        paymentOrderListError,
        salesOrderListError,
        orderDetailError,
        paymentStatusPolling,
        orderStatistics,
        orderStatisticsLoading,
        orderStatisticsError,

        // 计算属性
        hasPaymentOrderList,
        hasSalesOrderList,
        paymentOrderCount,
        salesOrderCount,
        canOperateOrder,
        paymentOrderListByStatus,
        salesOrderListByStatus,
        pendingPaymentOrders,
        rentingOrders,
        pendingConfirmOrders,
        formattedPaymentOrderList,
        formattedOrderStatistics,

        // 数据加载
        loadPaymentOrderList,
        loadSalesOrderList,
        loadOrderDetail,
        refreshPaymentOrderList,
        refreshSalesOrderList,
        loadOrderStatistics,
        refreshOrderStatistics,

        // 订单操作
        createRentalOrder,
        buyerCompleteRental,
        sellerConfirmReturn,
        cancelOrder,
        finalConfirmOrder,
        deleteOrder,
        cancelRefund,

        // 轮询
        startPaymentStatusPolling,
        stopPaymentStatusPolling,
        stopAllPaymentStatusPolling,

        // 工具方法
        findPaymentOrderById,
        findSalesOrderById,
        updateOrderInList,
        getOrderStatusText,
        getPaymentMethodText,

        // 状态重置
        clearOrderCache,
        resetPaymentOrderListState,
        resetSalesOrderListState,
        resetAllState
    }
}) 
